import type {AutomationNode, NodeConnection} from 'crqlar-react-common/dist/models/automation/automation';

export interface AgentImage {
    id: string;
    name: string;
    mimeType: string;
    data: string;
}

export interface AgentQuestion {
    question: string;
    options: string[];
}

/** One recorded edit of the backend's working copy, replayed by `applyAgentOperations`. */
export interface AgentOperation {
    kind: 'add' | 'update' | 'remove' | 'connect' | 'disconnect';
    /** add: temporary id (`new-1`, `new-2`, …) mapped to a UUID here; update/remove: an existing id. */
    nodeId?: string;
    /** add */
    nodeType?: string;
    /** add: required; update: optional rename. */
    label?: string;
    /** add: the full initial config; update: only the keys to set, `null` removes a key. */
    config?: Record<string, unknown>;
    /** add: place the new node below this node. */
    afterNodeId?: string;
    /** connect/disconnect; `branch` is 'true' | 'false' for If sources and '' otherwise. */
    source?: string;
    target?: string;
    branch?: string;
}

export interface AgentUsage {
    modelCalls?: number;
    toolCalls?: number;
    promptTokens?: number;
    outputTokens?: number;
    durationMs?: number;
}

export interface AgentReply {
    message: string;
    questions: AgentQuestion[];
    operations: AgentOperation[];
    notes: string[];
    usage?: AgentUsage;
}

export interface AgentProgress {
    step: number;
    tool: string;
    label: string;
}

/** `apply` edits the draft as soon as the reply arrives; `propose` waits for the user's Apply. Informational for the backend. */
export type AgentMode = 'apply' | 'propose';

export interface AgentMessage {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    images?: AgentImage[];
    questions?: AgentQuestion[];
    notes?: string[];
    applied?: string[];
    /** Sent with the conversation but never rendered — the repair turn's messages. */
    hidden?: boolean;
}

export interface AgentDraft {
    flowId: string;
    revision: number;
    nodes: AutomationNode[];
    connections: NodeConnection[];
    selectedNodeIds: string[];
}

export interface AgentApplyResult {
    changedNodeIds: string[];
    summary: string[];
}

/** Held back for the user's Apply, with the draft the operations were computed against. */
export interface AgentProposal {
    draft: AgentDraft;
    operations: AgentOperation[];
    summary: string[];
}

/** Thrown when the draft moved on under a reply; matched by identity — never auto-repaired, discards a proposal. */
export const FLOW_CHANGED_MESSAGE = 'The flow changed while I was responding. Send the request again so I can use your latest draft.';
